import { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import type { Centre } from '@/api/centresApi';
import { Seo } from '@/seo/Seo';
import {
  breadcrumbSchema,
  collectionPageSchema,
  faqSchema,
  serviceSchema,
} from '@/seo/schemas';
import { findCity, CITY_SLUGS } from '@/data/cities';
import { useSiteConfig } from '@/context/SiteConfigContext';
import { resolveDefaultImages } from '@/utils/centreImages';
import { findSector, sectorsByCity, SECTORS } from '@/data/sectors';
import { PROBLEMS } from '@/data/problems';
import { TREATMENT_APPROACHES } from '@/data/treatments';
import { parseCityAndSector, parseServiceCityAndSector } from '@/utils/slug';
import { useCatalog } from '@/context/CatalogContext';
import { TOP_SERVICE_SLUGS } from '@/seo/topServices';
import { CITY_PREFIX, cityPath, sectorPath, centreSeoPath } from '@/utils/seoPaths';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { CentreList } from '@/components/seo/CentreList';
import { LIST_LIMIT, RADIUS_KM, robotsFor, type LocationPageKind } from '@/seo/inventoryGate';
import { RelatedSearches } from '@/components/seo/RelatedSearches';
import { SeoPage, SeoHero, SeoSection, SeoFaq } from '@/components/seo/SeoLayout';
import { LocalProof } from '@/components/seo/LocalProof';
import { collectLocalProof } from '@/seo/localProof';
import { clinicListSchema } from '@/seo/schemas';
import {
  cityTitle,
  cityH1,
  cityDescription,
  sectorTitle,
  sectorH1,
  sectorDescription,
  serviceTitle,
  serviceDescription,
  serviceDisplayName,
} from '@/seo/meta';
import { centresForServiceUrl } from '@/utils/catalogPaths';

interface SectorServiceProps {
  /** Whether the URL pattern is /physiotherapy-in-:city[-:sector] (true) or /:service-in-:city[-:sector] (false). */
  hubOnly?: boolean;
}

/**
 * Renders one of four URL shapes:
 *   /physiotherapy-in-:city                         → CityHub (NOT this page)
 *   /physiotherapy-in-:city-:sector                 → SectorService (hub for that sector)
 *   /:service-in-:city                              → SectorService (service+city)
 *   /:service-in-:city-:sector                      → SectorService (service+city+sector)
 *
 * The unified template adapts copy and schema based on which params are present.
 */
export default function SectorService({ hubOnly = false }: SectorServiceProps) {
  // Single `:slug` param routed via SeoDispatcher. Slug shapes:
  //   hubOnly=true:  "physiotherapy-in-noida-sector-18"   (delegated from CityHub)
  //   hubOnly=false: "back-pain-therapy-in-noida[-sector-18]"
  const { slug, subSlug } = useParams<{ slug: string; subSlug: string }>();
  const { default_centre_image } = useSiteConfig();
  const defaultImage = resolveDefaultImages(default_centre_image)[0];

  // Inventory-gated indexation for these templated location pages: null = not
  // loaded (indexable); [] after load = no local clinics → noindex until supply.
  const [nearbyCentres, setNearbyCentres] = useState<Centre[] | null>(null);
  // Service names + slugs come from the live catalog, the same source clinics
  // pick from, so the page queries the API with exactly what clinics store.
  const { serviceBySlug, categoryById, loading: catalogLoading } = useCatalog();

  let citySlug: string | undefined;
  let sectorSlug: string | undefined;
  let serviceSlugRaw: string | undefined;

  if (hubOnly) {
    const rest = slug?.startsWith(CITY_PREFIX) ? slug.slice(CITY_PREFIX.length) : '';
    if (subSlug) {
      // Nested form: /physiotherapy-in-<city>/<sub-region>
      citySlug = rest;
      sectorSlug = subSlug;
    } else {
      const parsed = parseCityAndSector(rest, CITY_SLUGS, SECTORS);
      citySlug = parsed.citySlug;
      sectorSlug = parsed.sectorSlug;
    }
  } else {
    const parsed = parseServiceCityAndSector(slug ?? '', CITY_SLUGS, SECTORS);
    citySlug = parsed.citySlug;
    sectorSlug = parsed.sectorSlug;
    serviceSlugRaw = parsed.serviceSlug;
  }

  const city = findCity(citySlug);
  if (!city) return <Navigate to="/centres" replace />;

  const sector = sectorSlug ? findSector(city.slug, sectorSlug) : undefined;
  if (sectorSlug && !sector) return <Navigate to={cityPath(city.slug)} replace />;

  const entry = !hubOnly && serviceSlugRaw ? serviceBySlug(serviceSlugRaw) : undefined;
  if (!hubOnly && serviceSlugRaw && !entry) {
    if (catalogLoading) {
      return (
        <SeoPage>
          <div data-loading className="py-24 text-center text-muted-foreground">
            Loading…
          </div>
        </SeoPage>
      );
    }
    return <Navigate to={cityPath(city.slug)} replace />;
  }
  const servicePathId = entry ? (entry.path_a.length ? 'A' : 'B') : undefined;
  const service = entry
    ? {
        slug: entry.slug,
        name: serviceDisplayName(entry.name),
        group:
          categoryById(servicePathId!, (servicePathId === 'A' ? entry.path_a : entry.path_b)[0] ?? '')
            ?.label ?? 'Physiotherapy',
      }
    : undefined;

  // Build canonical path
  const path = service
    ? sector
      ? `/${service.slug}-in-${city.slug}-${sector.slug}`
      : `/${service.slug}-in-${city.slug}`
    : sector
      ? sectorPath(city.slug, sector.slug)
      : cityPath(city.slug);

  // Headline / titles
  const locationLabel = sector ? `${sector.displayName}, ${city.name}` : city.name;
  const h1 = service
    ? `${service.name} in ${locationLabel}`
    : sector
      ? sectorH1(sector.displayName, city.name)
      : cityH1(city.name);

  const seoTitle = service
    ? serviceTitle(service.name, locationLabel)
    : sector
      ? sectorTitle(sector.displayName, city.name)
      : cityTitle(city.name);

  // The sector blurb belongs in the page body, not the snippet - it pushed
  // descriptions to 300+ characters and Google cut them mid-sentence.
  const description = service
    ? serviceDescription(service.name, locationLabel)
    : sector
      ? sectorDescription(sector.displayName, city.name, nearbyCentres?.length || undefined)
      : cityDescription(city.name, nearbyCentres?.length || undefined);

  const clinicHref = (c: Centre) => centreSeoPath(c) ?? `/centres/${c.id}`;
  const clinicUrl = (c: Centre) => `https://www.recovry.in${clinicHref(c)}`;
  const proof = collectLocalProof(nearbyCentres ?? [], clinicHref);

  const near = sector ? { lat: sector.lat, lng: sector.lng } : { lat: city.lat, lng: city.lng };
  // Service pages have many near-identical siblings per city, so they need
  // more clinics than a plain sub-region hub before they are indexable.
  const pageKind: LocationPageKind = service ? 'service' : sector ? 'sector' : 'hub';
  const radiusKm = sector ? RADIUS_KM.sector : RADIUS_KM[pageKind];

  // Schema
  const schemas: Record<string, unknown>[] = [
    breadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Find a Centre', path: '/centres' },
      { name: city.name, path: cityPath(city.slug) },
      ...(sector
        ? [{ name: sector.displayName, path: sectorPath(city.slug, sector.slug) }]
        : []),
      ...(service ? [{ name: service.name, path }] : []),
    ]),
    collectionPageSchema({
      name: h1,
      description,
      url: `https://www.recovry.in${path}`,
    }),
  ];

  if (service) {
    schemas.push(
      serviceSchema({
        name: `${service.name} - ${locationLabel}`,
        description: description,
        url: `https://www.recovry.in${path}`,
        areaServed: {
          name: locationLabel,
          ...(sector ? { lat: sector.lat, lng: sector.lng } : { lat: city.lat, lng: city.lng }),
        },
        priceRange: '₹500-₹1500',
      }),
    );
  }

  // FAQs - different for service vs hub pages
  const faqs = service
    ? [
        {
          question: `How much does ${service.name.toLowerCase()} cost in ${locationLabel}?`,
          answer: `Most Recovry-verified centres in ${locationLabel} charge between ₹500 and ₹1,500 per session for ${service.name.toLowerCase()}. Exact pricing is shown on each centre page before you book.`,
        },
        {
          question: `How many sessions of ${service.name.toLowerCase()} will I need?`,
          answer: `Typical course is 6-12 sessions for most musculoskeletal conditions. Your physiotherapist will give a personalised plan based on the assessment in your first visit.`,
        },
        {
          question: `Do I need a doctor's referral for ${service.name.toLowerCase()} in ${locationLabel}?`,
          answer: `No. In India, you can directly book a physiotherapy assessment without a doctor's referral. Many Recovry-verified centres in ${locationLabel} accept walk-ins or online bookings.`,
        },
        {
          question: `Is ${service.name.toLowerCase()} available at home in ${locationLabel}?`,
          answer: `Some Recovry-verified centres in ${locationLabel} offer at-home physiotherapy. Check the centre detail page for the home-visit tag.`,
        },
      ]
    : [
        {
          question: 'How do I check a physiotherapist is qualified?',
          answer:
            'Ask for their BPT or MPT degree and registration. Physiotherapists in India register with the Indian Association of Physiotherapists and, under the 2021 Act, with the National Commission for Allied and Healthcare Professions. Recovry checks these documents for every clinic before it is listed.',
          links: [
            { label: 'Indian Association of Physiotherapists', href: 'https://physiotherapyindia.org/' },
            { label: 'NCAHP', href: 'https://ncahp.abdm.gov.in/' },
          ],
        },
        {
          question: `How do I find the best physiotherapy clinic in ${locationLabel}?`,
          answer: `Browse Recovry-verified centres in ${locationLabel}, sort by distance, and check ratings + therapies available. Every centre is verified by our team.`,
        },
        {
          question: `What's the average cost of physiotherapy in ${locationLabel}?`,
          answer: `Most centres in ${locationLabel} charge ₹500-₹1,500 per session. Specialised services like neuro rehab and ACL recovery are at the higher end.`,
        },
        {
          question: `Do clinics in ${locationLabel} offer Sunday or after-work appointments?`,
          answer: `Many Recovry-verified centres in ${locationLabel} offer evening and weekend slots. Check each centre's opening hours on its detail page.`,
        },
        {
          question: `What conditions can I get treated in ${locationLabel}?`,
          answer: `${locationLabel} centres treat the full range of musculoskeletal, sports, neurological and post-surgical conditions - including ${city.commonAilments.slice(0, 3).join(', ').toLowerCase()}.`,
        },
      ];

  if (nearbyCentres?.length) {
    schemas.push(
      clinicListSchema({
        name: h1,
        url: `https://www.recovry.in${path}`,
        centres: nearbyCentres,
        linkFor: (c) => clinicUrl(c as Centre),
        defaultImage,
      }),
    );
  }
  schemas.push(faqSchema(faqs));

  // Related sectors / services
  const otherSectors = sectorsByCity(city.slug).filter((s) => s.slug !== sectorSlug);
  const topServiceLinks = TOP_SERVICE_SLUGS.filter((s) => s !== service?.slug).flatMap((s) => {
    const e = serviceBySlug(s);
    if (!e) return [];
    return [{
      label: `${serviceDisplayName(e.name)} in ${locationLabel}`,
      path: sector ? `/${s}-in-${city.slug}-${sector.slug}` : `/${s}-in-${city.slug}`,
    }];
  });

  return (
    <SeoPage>
      <Seo
        title={seoTitle}
        description={description}
        canonical={path}
        robots={robotsFor(pageKind, nearbyCentres?.length)}
        keywords={
          service
            ? `${service.name.toLowerCase()} in ${locationLabel.toLowerCase()}, ${service.name.toLowerCase()} clinic, best physiotherapist for ${service.name.toLowerCase()}, ${city.name.toLowerCase()} physiotherapy`
            : `physiotherapy in ${locationLabel.toLowerCase()}, physiotherapy clinic ${locationLabel.toLowerCase()}, physio near me ${city.name.toLowerCase()}`
        }
        schema={schemas as never}
      />

      <Breadcrumbs
        items={[
          { name: 'Home', path: '/' },
          { name: 'Find a Centre', path: '/centres' },
          { name: city.name, path: cityPath(city.slug) },
          ...(sector
            ? [{ name: sector.displayName, path: sectorPath(city.slug, sector.slug) }]
            : []),
          ...(service ? [{ name: service.name, path }] : []),
        ]}
      />

      <SeoHero
        title={h1}
        lede={
          service
            ? `Verified clinics in ${locationLabel} offering ${service.name.toLowerCase()}. ${
                sector?.blurb ?? ''
              } Compare pricing, sort by distance, and book online.`.replace(/\s+/g, ' ')
            : sector?.blurb || city.intro
        }
        tags={service ? [service.group] : city.commonAilments.slice(0, 4)}
        tagsLabel={service ? 'Part of' : `Commonly treated in ${city.name}`}
        primaryCta={{ label: `All clinics in ${city.name}`, to: cityPath(city.slug) }}
        secondaryCta={{ label: 'List your clinic', to: '/partners' }}
      />

      <CentreList
        service={service?.slug}
        near={near}
        radiusKm={radiusKm}
        onResults={setNearbyCentres}
        limit={LIST_LIMIT[pageKind]}
        linkFor={centreSeoPath}
        heading={
          service
            ? `Clinics for ${service.name} in ${locationLabel}`
            : `Verified clinics in ${locationLabel}`
        }
        subheading={`Closest to ${locationLabel} first. Tap a clinic to see services, pricing and open hours.`}
        moreHref={cityPath(city.slug)}
        moreLabel={`See every clinic in ${city.name}`}
        emptyMessage={
          service
            ? `No verified clinic in ${locationLabel} lists ${service.name.toLowerCase()} yet. Try the city-wide listings, or list your clinic here.`
            : `No Recovry-verified clinics in ${locationLabel} yet. We onboard new clinics every week - or list yours and be the first here.`
        }
      />

      <LocalProof data={proof} place={locationLabel} />

      {service && (
        <SeoSection title={`What ${service.name.toLowerCase()} involves`}>
          <div className="rounded-2xl border bg-card p-5 shadow-sm sm:p-6">
            <p className="max-w-3xl leading-relaxed text-muted-foreground">
              {service.name} is part of Recovry&apos;s{' '}
              <Link
                to={centresForServiceUrl(service.slug, servicePathId)}
                className="font-medium text-primary hover:underline"
              >
                {service.group}
              </Link>{' '}
              care. A course starts with an assessment, then hands-on or exercise-based
              treatment and a home program you keep doing between visits. Most people notice a
              meaningful change within 4-8 sessions.
            </p>
          </div>
        </SeoSection>
      )}

      <SeoSection
        title={`How physiotherapists in ${locationLabel} treat ${service ? service.name.toLowerCase() : 'patients'}`}
        description="The techniques you can expect during a course of treatment."
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {TREATMENT_APPROACHES.slice(0, 4).map((t) => (
            <div key={t.name} className="rounded-xl border bg-card p-5 shadow-sm">
              <h3 className="font-semibold text-foreground">{t.name}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                {t.description}
              </p>
            </div>
          ))}
        </div>
      </SeoSection>

      <SeoSection title={`${service ? service.name : 'Physiotherapy'} in ${locationLabel}: common questions`}>
        <SeoFaq items={faqs} />
      </SeoSection>

      {/* Related */}
      <RelatedSearches
        groups={[
          ...(otherSectors.length > 0
            ? [
                {
                  heading: `Other localities in ${city.name}`,
                  links: otherSectors.slice(0, 8).map((s) => ({
                    label: service
                      ? `${service.name} in ${s.displayName}`
                      : `Physiotherapy in ${s.displayName}`,
                    path: service
                      ? `/${service.slug}-in-${city.slug}-${s.slug}`
                      : sectorPath(city.slug, s.slug),
                  })),
                },
              ]
            : []),
          {
            heading: 'Related services',
            links: topServiceLinks.slice(0, 6),
          },
          {
            heading: 'Related conditions',
            links: PROBLEMS.slice(0, 6).map((p) => ({
              label: `${p.name} in ${city.name}`,
              path: `/conditions/${p.slug}-in-${city.slug}`,
            })),
          },
        ]}
      />
    </SeoPage>
  );
}
